import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  Avatar,
  Checkbox,
  FormControlLabel,
  Divider,
} from "@mui/material";
import { Add, Delete, GroupAdd, Edit } from "@mui/icons-material";
import api from "../../api";

export default function ExamManagement() {
  const [exams, setExams] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    exam_name: "",
    exam_date: "",
    exam_time: "",
    total_marks: 100,
    pass_marks: 40,
    venue: "",
  });
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [filteredApplicants, setFilteredApplicants] = useState([]);
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [msg, setMsg] = useState(null);
  const [showPaidOnly, setShowPaidOnly] = useState(true);
  const [selectAll, setSelectAll] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ ...form });
  const [assignedApplicants, setAssignedApplicants] = useState([]);

  const schoolId = localStorage.getItem("school_id");

  // ✅ Fetch Exams
  const fetchExams = async () => {
    try {
      const res = await api.get(`/exam/list?school_id=${schoolId}`);
      const data = res.data;
      if (data?.status) {
        if (Array.isArray(data.data)) setExams(data.data);
        else if (typeof data.data === "object" && data.data !== null)
          setExams([data.data]);
        else setExams([]);
      } else setExams([]);
    } catch (err) {
      console.error("Error fetching exams:", err);
      setExams([]);
    }
  };

  // ✅ Create Exam
  const handleCreateExam = async () => {
    if (!form.exam_name || !form.exam_date) {
      setMsg({ type: "error", text: "Exam name & date are required." });
      return;
    }

    try {
      const payload = { ...form, school_id: schoolId };
      const res = await api.post("/exam/create", payload);
      if (res.data.status) {
        setMsg({ type: "success", text: "Exam created successfully." });
        setOpen(false);
        setForm({
          exam_name: "",
          exam_date: "",
          exam_time: "",
          total_marks: 100,
          pass_marks: 40,
          venue: "",
        });
        fetchExams();
      } else {
        setMsg({ type: "error", text: res.data.message || "Failed to create exam." });
      }
    } catch (err) {
      setMsg({
        type: "error",
        text: err.response?.data?.message || "Server error while creating exam.",
      });
    }
  };

  // ✅ Open Edit Dialog
  const openEditDialog = (exam) => {
    setEditForm({
      id: exam.id,
      exam_name: exam.exam_name,
      exam_date: exam.exam_date,
      exam_time: exam.exam_time,
      total_marks: exam.total_marks,
      pass_marks: exam.pass_marks,
      venue: exam.venue,
    });
    setEditOpen(true);
  };

  // ✅ Update Exam
  const handleUpdateExam = async () => {
    if (!editForm.exam_name || !editForm.exam_date) {
      setMsg({ type: "error", text: "Exam name & date are required." });
      return;
    }

    try {
      const payload = { ...editForm, school_id: schoolId };
      const res = await api.post("/exam/update", payload); // your backend endpoint
      if (res.data.status) {
        setMsg({ type: "success", text: "Exam updated successfully." });
        setEditOpen(false);
        fetchExams();
      } else {
        setMsg({
          type: "error",
          text: res.data.message || "Failed to update exam.",
        });
      }
    } catch (err) {
      setMsg({
        type: "error",
        text: err.response?.data?.message || "Error updating exam.",
      });
    }
  };

  // ✅ Fetch Applicants (filtered)
  const fetchApplicants = async (exam) => {
    setSelectedExam(exam);
    try {
      // 🔹 All applicants
      const res = await api.get(`/application/list?school_id=${schoolId}`);
      const data = res.data;
      let all = [];
      let paid = [];

      if (data?.status && Array.isArray(data.data)) {
        all = data.data;
        paid = all.filter((x) => x.payment_status === "paid");
      }

      setApplicants(all);
      setFilteredApplicants(paid);

      // 🔹 Assigned applicants
      const assignedRes = await api.get(
        `/exam/assigned?exam_id=${exam.id}&school_id=${schoolId}`
      );
      const assignedList = assignedRes.data?.data || [];

      // ✅ convert IDs to string for accurate matching
      const assignedIds = assignedList.map((x) => String(x.application_id));
      setAssignedApplicants(assignedIds);

      setAssignOpen(true);
      setSelectedApplicants([]);
      setSelectAll(false);
    } catch (err) {
      console.error("Error fetching applicants:", err);
      setApplicants([]);
      setFilteredApplicants([]);
    }
  };

  // ✅ Filter toggle
  const handleFilterChange = (checked) => {
    setShowPaidOnly(checked);
    if (checked) setFilteredApplicants(applicants.filter((x) => x.payment_status === "paid"));
    else setFilteredApplicants(applicants);
    setSelectedApplicants([]);
    setSelectAll(false);
  };

  // ✅ Select All
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      const paidOnly = filteredApplicants.filter((x) => x.payment_status === "paid");
      setSelectedApplicants(paidOnly.map((x) => x.id));
    } else {
      setSelectedApplicants([]);
    }
  };

  // ✅ Assign Applicants
  const handleAssignApplicants = async () => {
    if (!selectedApplicants.length) {
      setMsg({ type: "error", text: "Select at least one applicant to assign." });
      return;
    }

    const payload = {
      exam_id: selectedExam.id,
      application_ids: selectedApplicants,
      school_id: schoolId,
    };

    try {
      const res = await api.post("/exam/assign", payload);
      const data = res.data;

      if (data.status) {
        // ✅ Backend থেকে message ও count আসবে
        const assignedCount = data.data?.assigned_count || 0;
        const totalSelected = selectedApplicants.length;
        const skippedCount = totalSelected - assignedCount;

        let message = "";
        if (assignedCount > 0 && skippedCount > 0)
          message = `${assignedCount} new applicants assigned. ${skippedCount} applicants were already assigned.`;
        else if (assignedCount > 0)
          message = `${assignedCount} applicants assigned successfully.`;
        else
          message = "All selected applicants were already assigned.";

        setMsg({ type: "success", text: message });
        setAssignOpen(false);
        setSelectedApplicants([]);
      } else {
        setMsg({
          type: "error",
          text: data.message || "Failed to assign applicants.",
        });
      }
    } catch (err) {
      console.error("Assign error:", err);
      setMsg({
        type: "error",
        text: err.response?.data?.message || "Error assigning applicants.",
      });
    }
  };


  useEffect(() => {
    fetchExams();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          🧾 Entrance Exam Management
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>
          Add Entrance Exam
        </Button>
      </Box>

      {/* Exam List */}
      <Paper sx={{ mt: 3, p: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Exam Name</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Venue</TableCell>
              <TableCell>Total Marks</TableCell>
              <TableCell>Pass Marks</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(exams) && exams.length > 0 ? (
              exams.map((exam) => (
                <TableRow key={exam.id}>
                  <TableCell>{exam.exam_name}</TableCell>
                  <TableCell>{exam.exam_date}</TableCell>
                  <TableCell>{exam.exam_time || "—"}</TableCell>
                  <TableCell>{exam.venue || "—"}</TableCell>
                  <TableCell>{exam.total_marks || "—"}</TableCell>
                  <TableCell>{exam.pass_marks || "—"}</TableCell>
                  <TableCell>
                    <IconButton color="primary" onClick={() => fetchApplicants(exam)}>
                      <GroupAdd />
                    </IconButton>
                    <IconButton
                      color="success"
                      onClick={() => openEditDialog(exam)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton color="error">
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No entrance exams found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Add Exam Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add New Entrance Exam</DialogTitle>
        <DialogContent>
          <TextField
            label="Exam Name"
            fullWidth
            margin="dense"
            value={form.exam_name}
            onChange={(e) => setForm({ ...form, exam_name: e.target.value })}
          />
          <TextField
            label="Exam Date"
            type="date"
            fullWidth
            margin="dense"
            InputLabelProps={{ shrink: true }}
            value={form.exam_date}
            onChange={(e) => setForm({ ...form, exam_date: e.target.value })}
          />
          <TextField
            label="Exam Time (e.g. 10:00 AM - 12:00 PM)"
            fullWidth
            margin="dense"
            value={form.exam_time}
            onChange={(e) => setForm({ ...form, exam_time: e.target.value })}
          />
          <TextField
            label="Total Marks"
            type="number"
            fullWidth
            margin="dense"
            value={form.total_marks}
            onChange={(e) => setForm({ ...form, total_marks: e.target.value })}
          />
          <TextField
            label="Pass Marks"
            type="number"
            fullWidth
            margin="dense"
            value={form.pass_marks}
            onChange={(e) => setForm({ ...form, pass_marks: e.target.value })}
          />
          <TextField
            label="Venue"
            fullWidth
            margin="dense"
            value={form.venue}
            onChange={(e) => setForm({ ...form, venue: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateExam}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✏️ Edit Exam Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Entrance Exam</DialogTitle>
        <DialogContent>
          {["exam_name", "exam_date", "exam_time", "total_marks", "pass_marks", "venue"].map((field) => (
            <TextField
              key={field}
              label={
                field === "exam_name"
                  ? "Exam Name"
                  : field === "exam_date"
                    ? "Exam Date"
                    : field === "exam_time"
                      ? "Exam Time"
                      : field === "total_marks"
                        ? "Total Marks"
                        : field === "pass_marks"
                          ? "Pass Marks"
                          : "Venue"
              }
              type={["exam_date", "total_marks", "pass_marks"].includes(field) ? (field === "exam_date" ? "date" : "number") : "text"}
              fullWidth
              margin="dense"
              InputLabelProps={field === "exam_date" ? { shrink: true } : {}}
              value={editForm[field]}
              onChange={(e) => setEditForm({ ...editForm, [field]: e.target.value })}
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleUpdateExam}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
      {/* Assign Applicants Dialog */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          Assign Applicants — {selectedExam?.exam_name}
          {assignedApplicants.length > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.9rem", mt: 0.5 }}
            >
              {assignedApplicants.length} applicant(s) already assigned
            </Typography>
          )}
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={showPaidOnly}
                  onChange={(e) => handleFilterChange(e.target.checked)}
                />
              }
              label="Show only Paid Applicants"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectAll}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              }
              label="Select All (Paid Only)"
            />
          </Box>
          <Divider sx={{ mb: 2 }} />

          {/* ✅ Applicant List */}
          <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
            {filteredApplicants.length > 0 ? (
              filteredApplicants.map((a) => {
                const isPaid = a.payment_status === "paid";
                const isSelected = selectedApplicants.includes(a.id);
                const isAssigned = assignedApplicants.includes(String(a.id)); // ✅ already assigned check

                return (
                  <Box
                    key={a.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      py: 1.2,
                      px: 2,
                      mb: 1,
                      borderRadius: 2,
                      bgcolor: isAssigned
                        ? "#f2f2f2"
                        : isPaid
                          ? "#f9f9f9"
                          : "#fff5f5",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      opacity: isAssigned ? 0.7 : 1,
                    }}
                  >
                    {/* ✅ Checkbox show only if NOT assigned */}
                    {!isAssigned && (
                      <Checkbox
                        disabled={!isPaid}
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked)
                            setSelectedApplicants([...selectedApplicants, a.id]);
                          else
                            setSelectedApplicants(
                              selectedApplicants.filter((id) => id !== a.id)
                            );
                        }}
                        sx={{ mr: 2 }}
                      />
                    )}

                    {/* Avatar */}
                    <Avatar
                      src={a.photo ? `https://dgps.co.in/${a.photo}` : ""}
                      sx={{ width: 42, height: 42, mr: 2 }}
                    />

                    {/* Applicant Info */}
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={600} textTransform="uppercase">
                        {a.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {a.class_applied} • {a.mobile_no}
                      </Typography>
                    </Box>

                    {/* ✅ Status label */}
                    {isAssigned ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#777",
                            fontWeight: 600,
                            fontStyle: "italic",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          🔒 Already Assigned
                        </Typography>
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          color: isPaid ? "green" : "red",
                          fontWeight: 600,
                        }}
                      >
                        {isPaid ? "PAID" : "UNPAID"}
                      </Typography>
                    )}
                  </Box>
                );
              })
            ) : (
              <Typography color="text.secondary">No applicants found.</Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssignApplicants}>
            Assign Selected
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar */}
      <Snackbar
        open={!!msg}
        autoHideDuration={3000}
        onClose={() => setMsg(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {msg && (
          <Alert severity={msg.type} sx={{ width: "100%" }}>
            {msg.text}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}
